
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Crown, Clock, CheckCircle } from "lucide-react";
import { UserMembership } from "@/hooks/useUserMemberships";

interface MembershipSelectorProps {
  memberships: UserMembership[];
  selectedMembershipId: string | null;
  onSelectMembership: (membershipId: string | null) => void;
  onPayWithMembership: () => void;
}

const MembershipSelector = ({
  memberships,
  selectedMembershipId,
  onSelectMembership,
  onPayWithMembership
}: MembershipSelectorProps) => {
  // Filter out memberships with null membership data
  const validMemberships = memberships.filter(membership => {
    if (!membership.membership) {
      console.warn('Membership with null data found in selector:', membership.id);
      return false;
    }
    return true;
  });

  if (!validMemberships || validMemberships.length === 0) {
    return null;
  }

  const getSessionsDisplay = (membership: UserMembership) => {
    // Add null check for membership.membership
    if (!membership.membership) {
      return 'Invalid membership';
    }

    const { membership_type, session_count } = membership.membership;
    const { sessions_remaining, sessions_used } = membership;

    if (membership_type === 'unlimited') {
      return 'Unlimited sessions';
    }

    if (membership_type === 'pack_of_sessions') {
      return `${sessions_remaining || 0} sessions remaining`;
    }

    if (membership_type === 'sessions_per_month') {
      const totalSessions = session_count || 0;
      const usedThisMonth = sessions_used || 0;
      const remainingThisMonth = Math.max(0, totalSessions - usedThisMonth);
      return `${remainingThisMonth}/${totalSessions} sessions this month`;
    }

    return `${sessions_remaining || 0} sessions remaining`;
  };

  const getMembershipTypeDisplay = (type: string) => {
    switch (type) {
      case 'pack_of_sessions':
        return 'Session Pack';
      case 'sessions_per_month':
        return 'Monthly Plan';
      case 'unlimited':
        return 'Unlimited';
      default:
        return type;
    }
  };

  const canUseMembership = (membership: UserMembership) => {
    // Add null check for membership.membership
    if (!membership.membership) {
      return false;
    }

    const { membership_type } = membership.membership;
    const { sessions_remaining, expires_at } = membership;

    // Check if membership is expired
    if (expires_at && new Date(expires_at) < new Date()) {
      return false;
    }

    // For unlimited memberships, always available if not expired
    if (membership_type === 'unlimited') {
      return true;
    }

    // For session-based memberships, check if sessions are available
    return (sessions_remaining || 0) > 0;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Crown className="h-5 w-5 text-yellow-600" />
          <span>Use Your Membership</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600">
          You have active memberships that can be used for this booking:
        </p>
        
        <div className="space-y-3">
          {validMemberships.map((userMembership) => {
            const isSelected = selectedMembershipId === userMembership.id;
            const canUse = canUseMembership(userMembership);
            
            return (
              <div
                key={userMembership.id}
                className={`border rounded-lg p-4 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : canUse
                    ? 'border-gray-200 hover:border-gray-300'
                    : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
                }`}
                onClick={() => canUse && onSelectMembership(isSelected ? null : userMembership.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-medium">{userMembership.membership?.name || 'Unknown Membership'}</h4>
                      {isSelected && <CheckCircle className="h-4 w-4 text-blue-600" />}
                    </div>
                    
                    {userMembership.membership?.description && (
                      <p className="text-sm text-gray-600 mb-2">
                        {userMembership.membership.description}
                      </p>
                    )}
                    
                    <div className="flex items-center space-x-4 text-sm">
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span>{getSessionsDisplay(userMembership)}</span>
                      </div>
                      
                      <Badge variant={canUse ? "default" : "secondary"}>
                        {getMembershipTypeDisplay(userMembership.membership?.membership_type || 'unknown')}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                {!canUse && (
                  <div className="mt-2 text-xs text-red-600">
                    {userMembership.expires_at && new Date(userMembership.expires_at) < new Date()
                      ? 'Membership expired'
                      : 'No sessions remaining'
                    }
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Removed the "Book with Membership" button from here as requested */}
        {/*
        {selectedMembershipId && (
          <div className="border-t pt-4">
            <Button 
              onClick={onPayWithMembership}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              Book with Membership
            </Button>
            <p className="text-xs text-gray-500 text-center mt-2">
              No additional payment required
            </p>
          </div>
        )}
        */}
        
        {/* Only show "Pay as usual instead" if a membership is selected */}
        {selectedMembershipId && (
          <div className="border-t pt-4">
            <Button 
              variant="outline" 
              onClick={() => onSelectMembership(null)}
              className="w-full"
            >
              Pay as usual instead
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MembershipSelector;
